import React, { useEffect } from "react";
import {
  Box,
  Button,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  ArrowDropDown,
  AssignmentTurnedInOutlined,
  EditOutlined,
  Label,
  PauseCircleOutline,
  Print,
  Receipt,
  RequestQuote,
  SearchOffOutlined,
  Undo,
} from "@mui/icons-material";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
// import EnableDisableButton from "components/Shared/EnableDisableButton";
import ShipmentBasicDetail from "./ShipmentComponents/ShipmentBasicDetail";

import {
  useGetShipmentDetailQuery,
  useLazyListShipmentPricesQuery,
  useMarkShipmentReturningMutation,
  useUpdateShipmentMutation,
} from "store/services/shipmentApi";
import { useAddNoteSourceMutation } from "store/services/noteApi";
import {
  ShipmentDetailContext,
  useShipmentDetailContext,
} from "contexts/ShipmentDetailContext";

import GenerateShipmentLabel from "./ShipmentComponents/GenerateShipmentLabel";
import ShipmentDetailTab from "./ShipmentComponents/ShipmentDetailTab";

import { useAuth } from "hooks";
import { generatePodPrint } from "./ShipmentComponents/ShipmentPodPrint";
import { useGetDeliveryDetailByShipmentIdQuery } from "store/services/deliveryApi";
import { generatePORPrint } from "./ShipmentComponents/ShipmentPORPrint";
import FormDrawer from "components/Shared/FormDrawer";
import ShipmentCollectForm from "./ShipmentComponents/ShipmentCollectForm";
import ShipmentInvoicePrint2 from "./ShipmentComponents/ShipmentInvoicePrint2";
import { useSidebarLayoutContext } from "contexts/SidebarLayoutContext";
import ShipmentDetailClassicView from "./ShipmentComponents/ShipmentDetailClassicView";

const ShipmentDetailScreen = () => {
  const { id } = useParams();

  const { isCustomer, isDriver, isContractor, isVendor, isVendorStaff } =
    useAuth();
  const { shipmentDetailViewMode } = useSidebarLayoutContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // RTK Query
  const {
    data: ShipmentDetail,
    isLoading: loadingShipment,
    refetch: onShipmentDetailRefetch,
    error,
  } = useGetShipmentDetailQuery(id);

  const breadcrumb = [
    {
      title: "Shipment",
      path: isVendor
        ? "/app/vendor/shipment/list"
        : isCustomer
          ? "/app/customer/customer-shipment"
          : isContractor
            ? "/app/contractor/myShipments"
            : isDriver
              ? "/app/driver/myShipments"
              : isVendorStaff
                ? "/app/staff/shipment/list"
                : "/app/crm/shipment/list",
    },
    { title: "Shipment Detail" },
  ];

  return (
    <ShipmentDetailContext.Provider
      value={{ ShipmentDetail, loadingShipment, onShipmentDetailRefetch }}
    >
      <BreadcrumbLayout
        breadcrumbs={breadcrumb}
        isBusy={loadingShipment}
        headerActions={<PageActions />}
      >
        <BreadcrumbLayout.Error error={error} />
        <BreadcrumbLayout.Paper>
          {ShipmentDetail?.isMisplaced && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Alert severity="error" icon={<SearchOffOutlined />}>
                <Typography variant="body1">
                  <strong>Shipment Misplaced</strong>: This shipment has been
                  marked as misplaced. Please review or mark as found if
                  located.
                </Typography>
              </Alert>
            </Box>
          )}
          {isMobile || shipmentDetailViewMode === "classic" ? (
            <ShipmentDetailClassicView />
          ) : (
            <>
              <ShipmentBasicDetail />
              <Divider />
              <ShipmentDetailTab />
            </>
          )}
        </BreadcrumbLayout.Paper>
      </BreadcrumbLayout>
    </ShipmentDetailContext.Provider>
  );
};

const PageActions = () => {
  const [showLabelModal, setShowLabelModal] = React.useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openStatusDialog, setOpenStatusDialog] = React.useState(false);
  const [statusReason, setStatusReason] = React.useState("");
  const [actionAnchorEl, setActionAnchorEl] = React.useState(null);
  const [openHoldDialog, setOpenHoldDialog] = React.useState(false);
  const [holdReason, setHoldReason] = React.useState("");
  const [holdDuration, setHoldDuration] = React.useState("1d"); // "1d" | "2d" | "3d" | "custom"
  const [customHoldUntil, setCustomHoldUntil] = React.useState(null);
  // "hold" = putting on hold, "resume" = removing hold, "adjust" = changing duration while held.
  const [holdDialogMode, setHoldDialogMode] = React.useState("hold");
  const [openReturnDialog, setOpenReturnDialog] = React.useState(false);
  const [returnReason, setReturnReason] = React.useState("");
  const [showCollectForm, setShowCollectForm] = React.useState(false);
  const [openMisplacedDialog, setOpenMisplacedDialog] = React.useState(false);
  const [misplacedReason, setMisplacedReason] = React.useState("");

  const { ShipmentDetail, loadingShipment, onShipmentDetailRefetch } =
    useShipmentDetailContext();
  const [updateShipment, { isLoading: updatingShipment }] =
    useUpdateShipmentMutation();
  const [addNote, { isLoading: addingNote }] = useAddNoteSourceMutation();
  const { isVendor, isCustomer, isDriver, isVendorStaff, isAccountAdmin } =
    useAuth();
  const { loggedInUser, viewEntity } = useAuth();
  const { shipmentDetailViewMode, setShipmentDetailViewMode } = useSidebarLayoutContext();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [fetchPrices, { data: pricesRes, isLoading: loadingPrices }] =
    useLazyListShipmentPricesQuery();
  const [markShipmentReturning, { isLoading: markingReturning }] =
    useMarkShipmentReturningMutation();

  const handleCancelCollect = () => {
    setShowCollectForm(false);
    onShipmentDetailRefetch();
  };

  useEffect(() => {
    if (!ShipmentDetail?._id) {
      return;
    }
    fetchPrices({
      shipmentId: ShipmentDetail._id,
      limit: 100,
    });
  }, [ShipmentDetail?._id, fetchPrices]);

  const hasCollectedOrPaidPrice = React.useMemo(() => {
    if (ShipmentDetail?.hasCollectedOrPaidPrice === true) {
      return true;
    }
    const pricesList = Array.isArray(pricesRes?.data) ? pricesRes.data : [];
    const hasInPricesList = pricesList.some((p) => {
      const type = p?.chargeType;
      const status = String(p?.paymentStatus || "").toLowerCase();
      const remitStatus = String(p?.remitStatus || "").toLowerCase();
      const isChargeOrCOD = type === "Charge" || type === "Cash on Delivery";
      return (
        isChargeOrCOD &&
        (status === "collected" ||
          status === "paid" ||
          remitStatus === "transferred")
      );
    });
    if (hasInPricesList) {
      return true;
    }

    const payerDetails = ShipmentDetail?.payerDetails;
    if (payerDetails) {
      const allItems = [
        ...(payerDetails?.sender?.items || []),
        ...(payerDetails?.receiver?.items || []),
        ...(payerDetails?.vendor?.items || []),
      ];
      const hasInPayerDetails = allItems.some((item) => {
        const type = item?.chargeType;
        const status = String(item?.paymentStatus || "").toLowerCase();
        const isChargeOrCOD =
          !type || type === "Charge" || type === "Cash on Delivery";
        return isChargeOrCOD && (status === "collected" || status === "paid");
      });
      if (hasInPayerDetails) return true;
    }

    return false;
  }, [ShipmentDetail, pricesRes?.data]);

  const basePath = isVendor
    ? "/app/vendor/shipment"
    : isCustomer
      ? "/app/customer/shipment"
      : "/app/crm/shipment";

  const handleToggleShipmentStatus = async (reason = "") => {
    if (ShipmentDetail?.isActive && hasCollectedOrPaidPrice) {
      toast.error(
        "Cannot disable shipment because one or more charges or COD amounts have been collected or paid."
      );
      return;
    }
    try {
      await updateShipment({
        _id: ShipmentDetail._id,
        isActive: !ShipmentDetail.isActive,
        shipmentComment: reason,
      }).unwrap();
      toast.info(
        `Shipment ${ShipmentDetail.isActive ? "Disabled" : "Enabled"}!`
      );
      onShipmentDetailRefetch();
    } catch (error) {
      toast.error(error?.data?.message ?? "Could Not Update Status");
    }
  };

  const handleGeneratePod = (shipmentDetail) => {
    generatePodPrint(shipmentDetail, deliveryDetail);
  };

  const handleGeneratePor = (shipmentDetail) => {
    generatePORPrint(shipmentDetail);
  };

  const handleToggleOnHold = async () => {
    try {
      const puttingOnHold = holdDialogMode === "hold";

      // Build hold-lifecycle payload.
      //   "hold"    -> set isOnHold=true + duration
      //   "resume"  -> set isOnHold=false
      //   "adjust"  -> leave isOnHold as-is, only update duration
      const holdPayload = {};
      if (holdDialogMode !== "resume") {
        if (holdDuration === "custom") {
          if (!customHoldUntil || customHoldUntil <= new Date()) {
            toast.error("Please pick a release date & time in the future");
            return;
          }
          holdPayload.holdUntil = customHoldUntil.toISOString();
        } else {
          holdPayload.holdDurationDays = parseInt(holdDuration, 10); // 1 | 2 | 3
        }
      }

      await updateShipment({
        _id: ShipmentDetail._id,
        ...(holdDialogMode !== "adjust" && { isOnHold: puttingOnHold }),
        ...holdPayload,
        shipmentComment: holdReason,
      }).unwrap();

      const noteIntro =
        holdDialogMode === "resume"
          ? "Reason for resuming"
          : holdDialogMode === "adjust"
            ? "Reason for adjusting hold"
            : "Reason for putting on hold";
      const noteTail =
        holdDialogMode === "resume"
          ? ""
          : ` (auto-release: ${holdDuration === "custom"
            ? customHoldUntil?.toLocaleString()
            : `${holdDuration.replace("d", "")} day(s)`
          })`;

      await addNote({
        source: "Shipment",
        sourceRef: ShipmentDetail._id,
        content: `<p>${noteIntro}: ${holdReason}${noteTail}</p>`,
        type: "Note",
        showClient: isCustomer || isVendor,
      }).unwrap();

      const toastMsg =
        holdDialogMode === "resume"
          ? "Shipment resumed"
          : holdDialogMode === "adjust"
            ? "Hold duration adjusted"
            : "Shipment put on hold";
      toast.success(toastMsg);
      handleCloseHoldDialog();
      onShipmentDetailRefetch();
    } catch (error) {
      toast.error(error?.data?.message ?? "Could Not Update OnHold status");
    }
  };

  const handleOpenHoldDialog = () => {
    // Putting on hold (off -> on) or resuming (on -> off).
    const mode = ShipmentDetail?.isOnHold ? "resume" : "hold";
    setHoldDialogMode(mode);
    setHoldDuration("1d");
    setCustomHoldUntil(null);
    setOpenHoldDialog(true);
    setActionAnchorEl(null);
  };

  const handleOpenAdjustHoldDialog = () => {
    setHoldDialogMode("adjust");
    // Pre-fill from the shipment's current hold lifecycle so the user sees
    // the existing values and only changes what they want.
    const days = ShipmentDetail?.holdDurationDays;
    if (days === 1 || days === 2 || days === 3) {
      setHoldDuration(`${days}d`);
      setCustomHoldUntil(null);
    } else if (ShipmentDetail?.holdUntil) {
      setHoldDuration("custom");
      setCustomHoldUntil(new Date(ShipmentDetail.holdUntil));
    } else {
      setHoldDuration("1d");
      setCustomHoldUntil(null);
    }
    setOpenHoldDialog(true);
    setActionAnchorEl(null);
  };

  const handleCloseHoldDialog = () => {
    setOpenHoldDialog(false);
    setHoldReason("");
    setHoldDuration("1d");
    setCustomHoldUntil(null);
    setHoldDialogMode("hold");
  };

  const handleOpenReturnDialog = () => {
    setOpenReturnDialog(true);
    setActionAnchorEl(null);
  };

  const handleCloseReturnDialog = () => {
    setOpenReturnDialog(false);
    setReturnReason("");
  };

  const handleMarkReturning = async () => {
    try {
      const { shipment } = await markShipmentReturning({
        shipmentId: ShipmentDetail._id,
        returnReason,
      }).unwrap();
      await addNote({
        source: "Shipment",
        sourceRef: ShipmentDetail._id,
        content: `<p>Reason for ${shipment.IsReturning ? "marking as returning" : "canceling return"
          }: ${returnReason}</p>`,
        type: "Note",
        showClient: true,
      }).unwrap();
      toast.success(
        shipment.IsReturning
          ? "Shipment marked as returning"
          : "Shipment return canceled"
      );
      setOpenReturnDialog(false);
      setReturnReason("");
      onShipmentDetailRefetch();
      if (ShipmentDetail?._id) {
        fetchPrices({
          shipmentId: ShipmentDetail._id,
          limit: 100,
        });
      }
    } catch (error) {
      console.error("markShipmentReturning error:", error);
      toast.error(
        error?.data?.message ||
        `Failed to ${ShipmentDetail.IsReturning
          ? "cancel return"
          : "mark shipment as returning"
        }`
      );
    }
  };

  const handleOpenMisplacedDialog = () => {
    setOpenMisplacedDialog(true);
    setActionAnchorEl(null);
  };

  const handleCloseMisplacedDialog = () => {
    setOpenMisplacedDialog(false);
    setMisplacedReason("");
  };

  const handleMarkMisplaced = async () => {
    try {
      await updateShipment({
        _id: ShipmentDetail._id,
        isMisplaced: !ShipmentDetail.isMisplaced,
        shipmentComment: misplacedReason,
      }).unwrap();
      await addNote({
        source: "Shipment",
        sourceRef: ShipmentDetail._id,
        content: `<p>Reason for marking as ${ShipmentDetail.isMisplaced ? "found" : "misplaced"
          }: ${misplacedReason}</p>`,
        type: "Note",
        showClient: isCustomer || isVendor,
      }).unwrap();
      toast.success(
        `Shipment ${ShipmentDetail.isMisplaced ? "marked as found" : "marked as misplaced"
        }`
      );
      setOpenMisplacedDialog(false);
      setMisplacedReason("");
    } catch (error) {
      toast.error(error?.data?.message ?? "Could not update misplaced status");
    }
  };

  const { data: deliveryDetail } = useGetDeliveryDetailByShipmentIdQuery(
    ShipmentDetail?._id,
    {
      skip: !ShipmentDetail?._id || loadingShipment,
    }
  );
  const handleViewModeChange = (_, newMode) => {
    if (!newMode || newMode === shipmentDetailViewMode) return;
    setShipmentDetailViewMode(newMode);
    toast.info(
      newMode === "classic" ? "Switched to Classic Shipment Detail Screen" : "Switched to Default Shipment Detail Screen"
    );
  };
  return (
    <div className="flex flex-wrap gap-2">
      {!isMobile && (
      <ToggleButtonGroup
        value={shipmentDetailViewMode}
        exclusive
        size="small"
        onChange={handleViewModeChange}
      >
        <ToggleButton value="default">Default</ToggleButton>
        <ToggleButton value="classic">Classic</ToggleButton>
      </ToggleButtonGroup>
      )}
      {!isDriver && !isCustomer && (
        <>
          <Button
            startIcon={<EditOutlined />}
            variant="contained"
            color="primary"
            onClick={() => setAnchorEl(null)}
            component={Link}
            to={`${basePath}/${ShipmentDetail?._id}/edit`}
            disabled={
              ((isVendor || isVendorStaff) &&
                ShipmentDetail?.status !== "Pending") ||
              ShipmentDetail?.status === "Delivered" ||
              ShipmentDetail?.isActive === false
            }
          >
            Edit
          </Button>
          <Button
            startIcon={<Print />}
            variant="contained"
            color="primary"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            disabled={ShipmentDetail?.isActive === false}
            endIcon={<ArrowDropDown />}
          >
            Print
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            className="mt-3"
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <MenuItem
              onClick={() => {
                setShowLabelModal(true);
                setAnchorEl(null);
              }}
            >
              <Label sx={{ mr: 1 }} /> Generate Shipment Label
            </MenuItem>
            <MenuItem
              onClick={() => {
                setShowInvoiceModal(true);
                setAnchorEl(null);
              }}
              disabled={loadingShipment || !ShipmentDetail}
            >
              <RequestQuote sx={{ mr: 1 }} /> Invoice Print
            </MenuItem>
            {ShipmentDetail?.status === "Delivered" && (
              <MenuItem
                onClick={() => {
                  handleGeneratePod({ ShipmentDetail });
                  setAnchorEl(null);
                }}
                disabled={loadingShipment || !ShipmentDetail}
              >
                <AssignmentTurnedInOutlined sx={{ mr: 1 }} /> Proof of Delivery
                (POD)
              </MenuItem>
            )}
            <MenuItem
              onClick={() => {
                handleGeneratePor(ShipmentDetail);
                setAnchorEl(null);
              }}
              disabled={loadingShipment || !ShipmentDetail}
            >
              <Receipt sx={{ mr: 1 }} /> Proof of Receipt (POR)
            </MenuItem>
          </Menu>
        </>
      )}
      {!isDriver && !isVendor && !isVendorStaff && (
        <>
          <Button
            variant="contained"
            disabled={ShipmentDetail?.isActive === false}
            color="primary"
            onClick={(event) => setActionAnchorEl(event.currentTarget)}
            endIcon={<ArrowDropDown />}
          >
            Action
          </Button>
          <Menu
            anchorEl={actionAnchorEl}
            open={Boolean(actionAnchorEl)}
            onClose={() => setActionAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            className="mt-1"
          >
            <Box px={2} py={1}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                startIcon={<PauseCircleOutline />}
                onClick={handleOpenHoldDialog}
                disabled={ShipmentDetail?.status === "Delivered"}
              >
                {ShipmentDetail?.isOnHold ? "Resume Shipment" : "Hold Shipment"}
              </Button>
            </Box>
            {ShipmentDetail?.isOnHold && (
              <Box px={2} py={1}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="primary"
                  startIcon={<PauseCircleOutline />}
                  onClick={handleOpenAdjustHoldDialog}
                  disabled={ShipmentDetail?.status === "Delivered"}
                >
                  Adjust Hold Duration
                </Button>
              </Box>
            )}
            {isAccountAdmin && (
              <Box px={2} py={1}>
                <Tooltip
                  title={
                    ShipmentDetail?.isActive && hasCollectedOrPaidPrice
                      ? "Cannot disable shipment because one or more charges or COD amounts have been collected or paid"
                      : ""
                  }
                  arrow
                >
                  <span>
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      disabled={
                        isVendor ||
                        ShipmentDetail?.status === "Delivered" ||
                        (ShipmentDetail?.isActive && hasCollectedOrPaidPrice)
                      }
                      onClick={() => {
                        if (!ShipmentDetail?.isActive) {
                          handleToggleShipmentStatus(); // Enable without reason
                          setActionAnchorEl(null);
                        } else {
                          if (hasCollectedOrPaidPrice) {
                            toast.error(
                              "Cannot disable shipment because one or more charges or COD amounts have been collected or paid."
                            );
                            return;
                          }
                          setOpenStatusDialog(true); // Open dialog for disable
                        }
                      }}
                      sx={{
                        width: "100%",
                        opacity:
                          isVendor ||
                          (ShipmentDetail?.isActive && hasCollectedOrPaidPrice)
                            ? 0.5
                            : 1,
                      }}
                    >
                      {ShipmentDetail?.isActive
                        ? "Disable Shipment"
                        : "Enable Shipment"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            )}
            <Box px={2} py={1}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={
                  ShipmentDetail?.status === "Pending" ||
                  ShipmentDetail?.status === "Delivered" ||
                  markingReturning
                }
                startIcon={<Undo />}
                onClick={handleOpenReturnDialog}
              >
                {ShipmentDetail?.IsReturning
                  ? "Continue Return"
                  : "Return Shipment"}
              </Button>
            </Box>
            <Box px={2} py={1}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                disabled={ShipmentDetail?.status === "Delivered"}
                startIcon={<SearchOffOutlined />}
                onClick={handleOpenMisplacedDialog}
              >
                {ShipmentDetail?.isMisplaced
                  ? "Mark as Found"
                  : "Mark as Misplaced"}
              </Button>
            </Box>
          </Menu>
          <Dialog
            open={openHoldDialog}
            onClose={handleCloseHoldDialog}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              {holdDialogMode === "resume"
                ? "Resume Shipment"
                : holdDialogMode === "adjust"
                  ? "Adjust Hold Duration"
                  : "Hold Shipment"}
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Reason"
                multiline
                rows={4}
                fullWidth
                value={holdReason}
                onChange={(e) => setHoldReason(e.target.value)}
                sx={{ mt: 1 }}
                required
              />
              {holdDialogMode !== "resume" && (
                <Box sx={{ mt: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Hold Duration"
                    value={holdDuration}
                    onChange={(e) => setHoldDuration(e.target.value)}
                    helperText={
                      holdDialogMode === "adjust"
                        ? "Update when this shipment should auto-resume"
                        : holdDuration === "custom"
                          ? "Pick a date & time to auto-release the hold"
                          : `Shipment will auto-resume after ${holdDuration.replace("d", "")} day(s)`
                    }
                  >
                    <MenuItem value="1d">1 day</MenuItem>
                    <MenuItem value="2d">2 days</MenuItem>
                    <MenuItem value="3d">3 days</MenuItem>
                    <MenuItem value="custom">Custom (date & time)</MenuItem>
                  </TextField>
                  {holdDuration === "custom" && (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DateTimePicker
                        label="Release at"
                        value={customHoldUntil}
                        onChange={(v) => setCustomHoldUntil(v)}
                        minDateTime={new Date()}
                        renderInput={(params) => <TextField {...params} />}
                        sx={{ mt: 2, width: "100%" }}
                      />
                    </LocalizationProvider>
                  )}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseHoldDialog}>No</Button>
              <Button
                onClick={handleToggleOnHold}
                variant="contained"
                disabled={
                  !holdReason.trim() ||
                  updatingShipment ||
                  addingNote ||
                  (holdDialogMode !== "resume" &&
                    holdDuration === "custom" &&
                    !customHoldUntil)
                }
              >
                {holdDialogMode === "adjust" ? "Save" : "Yes"}
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openMisplacedDialog}
            onClose={handleCloseMisplacedDialog}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              {ShipmentDetail?.isMisplaced
                ? "Mark as Found"
                : "Mark as Misplaced"}
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Reason"
                multiline
                rows={4}
                fullWidth
                value={misplacedReason}
                onChange={(e) => setMisplacedReason(e.target.value)}
                sx={{ mt: 1 }}
                required
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseMisplacedDialog}>No</Button>
              <Button
                onClick={handleMarkMisplaced}
                variant="contained"
                disabled={
                  !misplacedReason.trim() || updatingShipment || addingNote
                }
              >
                Yes
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openReturnDialog}
            onClose={handleCloseReturnDialog}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              {ShipmentDetail?.IsReturning
                ? "Cancel Return"
                : "Mark as Returning"}
            </DialogTitle>
            <DialogContent>
              <TextField
                label="Reason"
                multiline
                rows={4}
                fullWidth
                value={returnReason}
                onChange={(e) => setReturnReason(e.target.value)}
                sx={{ mt: 1 }}
                required
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseReturnDialog}>No</Button>
              <Button
                onClick={handleMarkReturning}
                variant="contained"
                disabled={
                  !returnReason.trim() || markingReturning || addingNote
                }
              >
                Yes
              </Button>
            </DialogActions>
          </Dialog>
          <Dialog
            open={openStatusDialog}
            onClose={() => setOpenStatusDialog(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>Deactivate Shipment</DialogTitle>
            <DialogContent>
              <TextField
                label="Reason for Deactivation"
                multiline
                rows={4}
                fullWidth
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                sx={{ mt: 1 }}
                required
              />
            </DialogContent>
            <DialogActions>
              <Button
                onClick={() => {
                  setOpenStatusDialog(false);
                  setStatusReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  if (hasCollectedOrPaidPrice) {
                    toast.error(
                      "Cannot disable shipment because one or more charges or COD amounts have been collected or paid."
                    );
                    return;
                  }
                  try {
                    await updateShipment({
                      _id: ShipmentDetail._id,
                      isActive: false,
                      shipmentComment: statusReason,
                    }).unwrap();
                    toast.info("Shipment Disabled!");
                    setOpenStatusDialog(false);
                    setStatusReason("");
                    setActionAnchorEl(null);
                    onShipmentDetailRefetch();
                  } catch (error) {
                    toast.error(
                      error?.data?.message ?? "Could Not Update Status"
                    );
                  }
                }}
                variant="contained"
                disabled={
                  !statusReason.trim() ||
                  updatingShipment ||
                  hasCollectedOrPaidPrice
                }
              >
                Deactivate
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
      {ShipmentDetail && (
        <GenerateShipmentLabel
          isOpen={showLabelModal}
          onClose={() => setShowLabelModal(false)}
          shipment={ShipmentDetail}
        />
      )}
      {showInvoiceModal && (
        <ShipmentInvoicePrint2
          isOpen={showInvoiceModal}
          onClose={() => setShowInvoiceModal(false)}
          ShipmentDetail={ShipmentDetail}
          pricesRes={pricesRes}
          loadingPrices={loadingPrices}
          entity={viewEntity?.entity}
          loggedInUser={loggedInUser}
        />
      )}
      <FormDrawer
        title="Shipment Collect"
        open={showCollectForm}
        onClose={handleCancelCollect}
      >
        <ShipmentCollectForm
          isBusy={updatingShipment}
          shipmentId={ShipmentDetail?._id}
          onCancel={handleCancelCollect}
        />
      </FormDrawer>
    </div>
  );
};

export default ShipmentDetailScreen;
